import { useMemo } from "react"
import {
  ConditionGroup,
  InsurancePoliciesResponse,
  InsurancePolicy,
  NextPageParams,
  PaginationMetadata,
} from "@/schema"
import { useDataTableStore } from "@/stores/data-table-store"
import { useInfiniteQuery, useQuery } from "@tanstack/react-query"

import { useApiContext } from "../use-api-context"
import {
  fetchHouseholdInsurancePolicies,
  fetchInsurancePolicies,
  fetchInsurancePolicy,
  fetchTrustInsurancePolicies,
} from "./api"

export function useInfiniteInsurancePolicies(enabled?: boolean) {
  const getContext = useApiContext()
  const { searchTerm, completedConditions, viewSort } = useDataTableStore()
  const computedFilterGroup = useMemo(
    () => ({ combinator: "and", conditions: completedConditions }) as ConditionGroup,
    [completedConditions]
  )

  return useInfiniteQuery<
    InsurancePoliciesResponse,
    Error,
    { insurance_policies: InsurancePolicy[]; pagination: PaginationMetadata },
    (string | ConditionGroup | undefined | { field?: string; direction?: "asc" | "desc" })[],
    NextPageParams | undefined
  >({
    queryKey: ["insurance_policies", "infinite", searchTerm, computedFilterGroup, viewSort],
    queryFn: async ({ pageParam }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchInsurancePolicies(context, computedFilterGroup, pageParam, searchTerm, viewSort)
    },
    enabled,
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.pagination?.next_page_params ?? undefined,
    select: (data) => ({
      insurance_policies: data.pages.flatMap((page) => page.insurance_policies),
      pagination: data.pages[data.pages.length - 1]?.pagination ?? { total_count: 0, next_page_params: null },
    }),
  })
}

export function useFetchHouseholdInsurancePolicies(householdId: string) {
  const getContext = useApiContext()

  return useQuery({
    queryKey: ["household", householdId, "insurance_policies"],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No API context found")
      return (await fetchHouseholdInsurancePolicies(context, householdId)).insurance_policies
    },
    enabled: !!householdId,
  })
}

export function useFetchInsurancePolicy(householdId: string, policyId?: string) {
  const getContext = useApiContext()

  return useQuery({
    queryKey: ["household", householdId, "insurance_policies", policyId],
    queryFn: async () => {
      const context = await getContext()
      if (!context || !policyId) throw new Error("No API context or policy found")
      return fetchInsurancePolicy(context, householdId, policyId)
    },
    enabled: !!(householdId && policyId),
  })
}

export function useFetchTrustInsurancePolicies(trustId: string) {
  const getContext = useApiContext()

  return useQuery({
    queryKey: ["insurance_policies", "trust", trustId],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No API context found")
      return (await fetchTrustInsurancePolicies(context, trustId)).insurance_policies
    },
    enabled: !!trustId,
  })
}
